import { Button } from "@msuite/ui/button";
import { DataGrid } from "@msuite/ui/data-grid";
import { useLoading } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { StateTag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import type { Tables } from "@msuite/utils/types";
import { Can } from "@/components/citadel";
import { client } from "@/hono";
import { usePartnershipDirection } from "@/hooks/use-partnership-direction";

/** Props Interface */
interface PartnerDirectionRowProps {
	orgId: string;
	organizationName: string;
	partnership: Tables<"organizationPartnerships">;
}

export function PartnerDirectionRow({
	orgId,
	organizationName,
	partnership,
}: PartnerDirectionRowProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const loader = useLoading();
	const { getRole, getDirectionLabel } = usePartnershipDirection();

	/** Functions */
	async function handleOnChangePartnershipState() {
		try {
			loader.start();
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].state.$patch({
				param: {
					orgId: partnership.hostOrgId,
					partnershipId: partnership.id,
				},
				json: {
					state: partnership.state === "active" ? "inactive" : "active",
				},
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate([orgId, partnership.id]) });
		} catch (error) {
			// `responseHandler` has already toasted the reason.
			console.error(error);
		} finally {
			loader.stop();
		}
	}

	/** Computed */
	// Only the host of a row may change its state; the counterpart's row is shown, not offered.
	const viewerHostsPartnership = getRole(partnership) === "host";

	/** Render */
	return (
		<DataGrid.Row>
			<DataGrid.Label>{getDirectionLabel(partnership, organizationName)}</DataGrid.Label>
			<DataGrid.Cell className="justify-between flex-row flex">
				<StateTag state={partnership.state} />
				{viewerHostsPartnership && (
					<Can
						action="update"
						subject="Partner"
					>
						{partnership.state !== "active" ? (
							<Button
								onClick={handleOnChangePartnershipState}
								isLoading={loader.isLoading}
							>
								{t("organization.activate_partnership")}
							</Button>
						) : (
							<Button
								onClick={handleOnChangePartnershipState}
								variant="danger-ghost"
								isLoading={loader.isLoading}
							>
								{t("organization.deactivate_partnership")}
							</Button>
						)}
					</Can>
				)}
			</DataGrid.Cell>
		</DataGrid.Row>
	);
}
